/**
 * PLAT-TOS-LAYERED-1 — the product terms addendum, shown the first time a
 * person opens a product and again whenever that product's addendum version
 * changes. Layered on top of the platform Terms (TosGate), never instead.
 *
 * Mounted by ProductGate once the firm-entitlement layer passes, so every
 * product-gated route gets it keyed by the ROUTE's product — not by which SPA
 * build is serving it (the QoE SPA also serves /valuation).
 *
 * State comes from /v1/me (`user.product_terms`). Absent (the claims-only E2E
 * bootstrap identity) is treated as "not known yet" and passes through: the
 * server is the gate (apps/api/product_terms_gate.py → 403
 * PRODUCT_TERMS_REQUIRED), the same call ProductGate makes for `products`.
 *
 * The page's own content is NOT rendered until accepted, so it cannot fire
 * product API calls that the server would refuse.
 */
import { useState, type ReactNode } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { acceptProductTerms } from '@/api/productTerms'
import { PRODUCT_TERMS_LABELS } from '@/lib/productTerms'
import type { ProductKey } from '@/lib/productNav'

interface ProductTermsGateProps {
  product: ProductKey
  children: ReactNode
}

export function ProductTermsGate({ product, children }: ProductTermsGateProps) {
  const { user, refreshUser } = useAuth()
  const [checked, setChecked] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const entry = user?.product_terms?.find(p => p.product === product)
  if (!entry || !entry.required) return <>{children}</>

  const label =
    user?.products?.find(p => p.product === product)?.label ?? PRODUCT_TERMS_LABELS[product]

  const handleAccept = async () => {
    if (!checked || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await acceptProductTerms(product, entry.version)
      await refreshUser()
    } catch (err) {
      setError(
        (err as { response?: { status?: number } })?.response?.status === 409
          ? 'These terms were just updated. Reload the page to read the current version.'
          : 'Something went wrong recording your acceptance. Please try again.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mx-auto max-w-xl p-6">
      <section
        className="rounded-xl border border-gray-200 bg-white p-6"
        aria-labelledby="product-terms-title"
      >
        <h1 id="product-terms-title" className="text-lg font-semibold text-gray-900">
          {label} terms
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          Before you use {label}, please review and accept its product terms. They add to the
          Terms of Service you have already accepted.
        </p>
        <p className="mt-3 text-sm">
          <a
            href={entry.terms_url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-[#0d1f3c] underline"
          >
            Read the {label} terms
          </a>
        </p>
        <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-lg p-3 hover:bg-gray-50">
          <input
            type="checkbox"
            className="mt-1 size-4 shrink-0 cursor-pointer"
            checked={checked}
            onChange={e => setChecked(e.target.checked)}
          />
          <span className="text-sm text-gray-700">
            I have read and agree to the {label} terms (version {entry.version}).
          </span>
        </label>
        {error && (
          <p className="mt-3 rounded bg-red-50 p-3 text-sm text-red-700" role="alert">{error}</p>
        )}
        <div className="mt-5 flex items-center gap-4">
          <button
            type="button"
            onClick={handleAccept}
            disabled={!checked || submitting}
            className="rounded-md bg-[#0d1f3c] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {submitting ? 'Saving…' : 'Accept & Continue'}
          </button>
          {!checked && !submitting && (
            <span className="text-sm text-gray-500" role="status">
              Tick the box to continue.
            </span>
          )}
        </div>
      </section>
    </div>
  )
}
